import { useEffect, useRef, useState } from "react";
import * as echarts from "echarts";

export default function LineChart({
  items,
  dispatch,
  questionState,
  isExplanation = false,
  presetValues,
  minMax,
  step = 1
}) {
  const [chartKey, setChartKey] = useState(0);
  const [openResetConfirm, setOpenResetConfirm] = useState(false);
  const chartRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragIndexRef = useRef(-1);
  const dataRef = useRef([]);
  const dispatchTimerRef = useRef(null);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 0
  );

  const isMobile = windowWidth < 900;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getMinMax = () => {
    if (isExplanation && presetValues?.length) {
      const min = Math.min(...presetValues);
      const max = Math.max(...presetValues);
      const buffer = Math.ceil(max * 0.1);
      return {
        min: Math.floor(min * 0.9),
        max: Math.ceil((max + buffer) / 10) * 10,
      };
    }
    if (minMax) return minMax;
    return { min: 0, max: 1000 };
  };

  const { min: rawMin, max: rawMax } = getMinMax();
  // const minY = Math.floor(rawMin / 10) * 10;
  // const maxY = Math.ceil(rawMax / 10) * 10;
  // ✅ pastikan step integer valid
  const stepNum = Math.max(1, Math.floor(step));

  // ✅ min/max dari backend tetap dipakai, tapi batas chart dibulatkan ke step
  const minY = Math.floor(rawMin / stepNum) * stepNum;
  const maxY = Math.ceil(rawMax / stepNum) * stepNum;

  const showIndicator = (chart, index, val) => {
    chart.setOption({
      series: [
        {
          markPoint: {
            data: [
              {
                coord: [index, val],
                symbol: "circle",
                symbolSize: 22,
                itemStyle: {
                  color: "#4CAF50",
                  borderColor: "#fff",
                  borderWidth: 3,
                  shadowBlur: 12,
                },
                label: { show: false },
              },
            ],
          },
        },
      ],
    });
  };

  const hideIndicator = (chart) => {
    chart.setOption({ series: [{ markPoint: { data: [] } }] });
  };

  // ✅ simpan props terbaru supaya handler pointer tidak pakai closure lama
  const latestPropsRef = useRef({
    dispatch,
    questionState,
  });

  useEffect(() => {
    latestPropsRef.current = { dispatch, questionState };
  }, [dispatch, questionState]);

  useEffect(() => {
    const dom = chartRef.current;
    if (!dom || items.length === 0) return;

    // ✅ biar drag mobile nggak dianggap scroll/zoom
    dom.style.touchAction = "none";

    const formatValue = (val) => {
      if (maxY >= 1_000_000) {
        const v = val / 1_000_000;
        const fixed = Number.isInteger(v) ? v.toFixed(0) : v.toFixed(1);
        return `${fixed} jt`;
      }
      if (maxY >= 1_000) return new Intl.NumberFormat("id-ID").format(val);
      return String(val);
    };

    // const parseAnswerText = (ans: any): number[] => {
    //   if (!ans || !Array.isArray(ans)) return [];
    //   return ans.map((v) => {
    //     const parsed = typeof v === "string" ? parseFloat(v) : v;
    //     return isNaN(parsed) ? 0 : parsed;
    //   });
    // };

    // const answerTextNumbers = parseAnswerText(questionState?.answerText);

    const len = items.length;

    // random harus dibuat dulu (seperti punyamu)
    // const randomValues = items.map(() => {
    //   if (maxY <= randomMin) return randomMin;
    //   return Math.floor(Math.random() * (maxY - randomMin + 1)) + randomMin;
    // });

    const randomValues = items.map(() => {
      // Edge-case: range tidak memungkinkan nilai > minY
      if (maxY <= minY) return minY;

      const stepsCount = Math.floor((maxY - minY) / stepNum);

      // Kalau stepsCount < 1 berarti (maxY - minY) < stepNum, jadi tidak ada step di atas minY
      if (stepsCount < 1) return minY;

      // ✅ mulai dari 1 supaya hasil random selalu > minY
      const k = 1 + Math.floor(Math.random() * stepsCount);
      return minY + k * stepNum;
    });

    // ✅ clamp + snap (default step=1)
    const clampY = (n) => {
      const stepNumVal = Number(step);
      const clamped = Math.max(minY, Math.min(maxY, n));

      if (!Number.isFinite(stepNumVal) || stepNumVal <= 1) return Math.round(clamped);

      // ✅ snap relatif ke minY biar konsisten
      const snapped = minY + Math.round((clamped - minY) / stepNumVal) * stepNumVal;

      // ✅ clamp lagi biar gak lewat maxY
      return Math.max(minY, Math.min(maxY, snapped));
    };

    const normalizeToLen = (arr, fallback) => {
      const out = new Array(len);
      for (let i = 0; i < len; i++) {
        const raw = arr?.[i];

        // null/undefined/"" dianggap kosong → fallback
        if (raw === null || raw === undefined || String(raw).trim() === "") {
          out[i] = fallback[i];
          continue;
        }

        const num = typeof raw === "number" ? raw : Number(raw);
        out[i] = Number.isFinite(num) ? clampY(num) : fallback[i];
      }
      return out;
    };

    // cek apakah user sudah pernah ngisi (mirip PieChart: kalau kosong → jangan dipakai)
    const answerRaw = questionState?.answerText;
    const hasUserAnswer =
      Array.isArray(answerRaw) &&
      answerRaw.length > 0 &&
      answerRaw.some((v) => v !== null && v !== undefined && String(v).trim() !== "");

    // normalized arrays
    const userValues = hasUserAnswer ? normalizeToLen(answerRaw, randomValues) : null;

    const hasPreset = Array.isArray(presetValues) && presetValues.length > 0;
    const presetNormalized = hasPreset ? normalizeToLen(presetValues, randomValues) : null;

    // ✅ final effective values (mirip piechart)
    const effectiveValues = isExplanation
      ? (presetNormalized ?? userValues ?? randomValues) // explanation: prioritas preset
      : (userValues ?? randomValues);                    // tryout: prioritas user

    dataRef.current = effectiveValues;

    const chart = echarts.init(dom);

    chart.setOption({
      tooltip: {
        trigger: "axis",
        confine: true,
        formatter: (params) => {
          const p = Array.isArray(params) ? params[0] : params;
          return `${p.name}<br/>Nilai: ${formatValue(p.value)}`;
        },
      },
      grid: {
        left: "8%",
        right: "4%",
        bottom: isMobile ? "20%" : "10%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: items,
        boundaryGap: false,
        axisLabel: {
          interval: 0,
          rotate: 45,
          fontSize: isMobile ? 12 : 14,
        },
      },
      yAxis: {
        type: "value",
        min: minY,
        max: maxY,
        axisLabel: { formatter: (value) => formatValue(value) },
      },
      series: [
        {
          type: "line",
          smooth: false,
          data: effectiveValues,
          symbolSize: 12,
          lineStyle: { width: 3, color: "#4CAF50" },
          itemStyle: { color: "#4CAF50", borderColor: "#fff", borderWidth: 2 },
          areaStyle: { color: "rgba(76,175,80,0.25)" },
          label: {
            show: true,
            position: "top",
            distance: 20,
            formatter: (params) => formatValue(params.value),
            fontSize: isMobile ? 11 : 13,
            fontWeight: "bold",
            color: "#333",
          },
          markPoint: { data: [] },
        },
      ],
    });

    // ✅ define sekali biar nggak scope error
    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);

    // ===== Explanation mode: no drag
    if (isExplanation) {
      dom.style.cursor = "default";
      return () => {
        if (dispatchTimerRef.current) clearTimeout(dispatchTimerRef.current);
        window.removeEventListener("resize", handleResize);
        chart.dispose();
      };
    }

    dom.style.cursor = "grab";

    let activePointerId = null;

    const getRelativePos = (evt) => {
      const rect = dom.getBoundingClientRect();
      return { x: evt.clientX - rect.left, y: evt.clientY - rect.top };
    };

    const endDrag = (evt) => {
      if (!isDraggingRef.current) return;
      if (evt && activePointerId !== null && evt.pointerId !== activePointerId) return;

      isDraggingRef.current = false;
      dragIndexRef.current = -1;

      hideIndicator(chart);
      dom.style.cursor = "grab";

      if (activePointerId !== null) {
        try { dom.releasePointerCapture(activePointerId); } catch { }
        activePointerId = null;
      }

      // ✅ dispatch langsung biar yang kesimpan selalu nilai terbaru (tanpa delay)
      if (dispatchTimerRef.current) {
        clearTimeout(dispatchTimerRef.current);
        dispatchTimerRef.current = null;
      }

      const cur = [...dataRef.current];

      // ✅ ambil props terbaru dari ref
      const { dispatch: d, questionState: qs } = latestPropsRef.current;

      if (d && qs) {
        d({
          type: "question/setQuestion",
          payload: { ...qs, answerText: cur.map(String) },
        });
      }
    };

    const handlePointerDown = (evt) => {
      if (evt.pointerType === "mouse" && evt.button !== 0) return;
      if (activePointerId !== null) return;

      const { x, y } = getRelativePos(evt);
      const point = chart.convertFromPixel("grid", [x, y]);
      if (!point) return;

      const idx = Math.round(point[0]);
      if (idx < 0 || idx >= dataRef.current.length) return;

      // hanya mulai drag kalau dekat titik
      const pixel = chart.convertToPixel("grid", [idx, dataRef.current[idx]]);
      if (!pixel) return;

      const distance = Math.hypot(x - pixel[0], y - pixel[1]);
      if (distance > 25) return;

      evt.preventDefault();

      activePointerId = evt.pointerId;
      try { dom.setPointerCapture(evt.pointerId); } catch { }

      isDraggingRef.current = true;
      dragIndexRef.current = idx;
      dom.style.cursor = "grabbing";
      showIndicator(chart, idx, dataRef.current[idx]);
    };

    const handlePointerMove = (evt) => {
      if (!isDraggingRef.current) return;
      if (activePointerId !== null && evt.pointerId !== activePointerId) return;

      evt.preventDefault();

      const { x, y } = getRelativePos(evt);
      const point = chart.convertFromPixel("grid", [x, y]);
      if (!point) return;

      // const rawValue = Math.max(minY, Math.min(maxY, point[1]));
      // const snappedValue = Math.round(rawValue);
      const snappedValue = clampY(point[1]); // ✅ pakai clamp dari props

      const updated = [...dataRef.current];
      updated[dragIndexRef.current] = snappedValue;
      dataRef.current = updated;

      chart.setOption({ series: [{ data: updated }] });
      showIndicator(chart, dragIndexRef.current, snappedValue);
    };

    const handlePointerUp = (evt) => endDrag(evt);
    const handlePointerCancel = (evt) => endDrag(evt);

    dom.addEventListener("pointerdown", handlePointerDown, { passive: false });
    dom.addEventListener("pointermove", handlePointerMove, { passive: false });
    dom.addEventListener("pointerup", handlePointerUp, { passive: false });
    dom.addEventListener("pointercancel", handlePointerCancel, { passive: false });
    dom.addEventListener("lostpointercapture", handlePointerCancel, { passive: false });

    return () => {
      dom.removeEventListener("pointerdown", handlePointerDown);
      dom.removeEventListener("pointermove", handlePointerMove);
      dom.removeEventListener("pointerup", handlePointerUp);
      dom.removeEventListener("pointercancel", handlePointerCancel);
      dom.removeEventListener("lostpointercapture", handlePointerCancel);

      window.removeEventListener("resize", handleResize);
      if (dispatchTimerRef.current) clearTimeout(dispatchTimerRef.current);
      chart.dispose();
    };
  }, [isMobile, minY, maxY, chartKey]);

  // ✅ Handler reset
  // klik tombol -> cuma buka confirm
  const handleResetClick = () => {
    questionState.answerText !== "" ? setOpenResetConfirm(true) : setChartKey((prev) => prev + 1);
  };

  // yang benar2 reset + kosongkan jawaban di redux
  const handleResetConfirm = () => {
    // 1) kosongkan state jawaban di redux
    if (dispatch && questionState) {
      dispatch({
        type: "question/setQuestion",
        payload: {
          ...questionState,
          answerText: "",
        },
      });
    }

    // 2) rerender chart (biar randomValues kepakai lagi)
    setChartKey((prev) => prev + 1);

    // 3) tutup dialog
    setOpenResetConfirm(false);
  };

  const handleResetCancel = () => setOpenResetConfirm(false);

  return (
    <div id="linechart-container" className="interactive chart-col-full">
      <div
        id="linechart"
        ref={chartRef}
        style={{ width: "100%", minHeight: 400, paddingRight: 10 }}
      />
      {!isExplanation && (
        <>
          <button
            onClick={handleResetClick}
            style={{
              marginBottom: 16,
              padding: "8px 16px",
              backgroundColor: "#1976d2",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 500,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#1565c0"; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#1976d2"; }}
          >
            Reset
          </button>

          {openResetConfirm && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1300,
              }}
              onClick={handleResetCancel}
            >
              <div
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "4px",
                  boxShadow: "0 5px 25px rgba(0, 0, 0, 0.3)",
                  minWidth: 300,
                  maxWidth: 500,
                  zIndex: 1301,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ padding: "20px 24px", borderBottom: "1px solid #e0e0e0" }}>
                  <h2 style={{ margin: 0, fontSize: 20, fontWeight: 500 }}>Reset jawaban?</h2>
                </div>
                <div style={{ padding: "20px 24px" }}>
                  <p style={{ margin: 0, color: "#666" }}>
                    Kalau kamu klik <b>Iya</b>, semua nilai line yang sudah kamu atur akan diacak.
                  </p>
                </div>
                <div style={{ padding: "12px 16px", display: "flex", justifyContent: "flex-end", gap: 8, borderTop: "1px solid #e0e0e0" }}>
                  <button
                    onClick={handleResetCancel}
                    style={{
                      padding: "6px 16px",
                      backgroundColor: "#f5f5f5",
                      color: "#333",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: 14,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#e0e0e0"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#f5f5f5"; }}
                  >
                    Batal
                  </button>
                  <button
                    onClick={handleResetConfirm}
                    style={{
                      padding: "6px 16px",
                      backgroundColor: "#d32f2f",
                      color: "#fff",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: 14,
                      fontWeight: 500,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#c62828"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#d32f2f"; }}
                  >
                    Iya, reset
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}